import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  LinearProgress,
  Stack,
  Button,
  linearProgressClasses,
  Slider,
  Drawer,
  CardActions,
  Tooltip,
  Typography,
  Chip
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import '../../Style/Pulse.css'
import {
  AddComment,
  Check,
  ContactSupport,
  Forum,
  KeyboardDoubleArrowDown
} from '@mui/icons-material'
import PulseComments from './PulseComment'
import InfoDialog from './InfoDialog'
import PulseAlertMessage from './Helpers/PulseAlertMessage'

const PulseComponent = ({
  pulsePrompt,
  pulseValues,
  comments = [],
  setComments,
  isAllowedToComment,
  displayName,
  uid,
  document,
  sectionIndex,
  pulseIndex,
  hasNextSection,
  goToNextSection,
  pulseTotalPrompts,
  section,
  isPreview
}) => {
  const [pollAnswers, setPollAnswers] = useState(
    pulseValues.map(value => {
      return {
        option: value,
        votes: 0
      }
    })
  )
  const [userVoted, setUserVoted] = useState(null)
  const [alert, setAlert] = useState(null)
  const [currentComment, setCurrentComment] = useState({
    message: '',
    messageType: 'Vote',
    uid,
    displayName
  })
  const [isAdmin, setIsAdmin] = useState(false)
  const [sortType, setSortType] = useState('All Comments')
  const [clicked, setClicked] = useState(false)
  const [open, setOpen] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [value, setValue] = useState(null)
  const [showInfo, setShowInfo] = useState(false)

  const toggleDrawer = open => event => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return
    }
    if (!open) {
      setShowEditModal(false)
    }
    setOpen(open)
  }

  const addComment = (comment, answer) => {
    if (!isAllowedToComment) {
      console.warn('User is not allowed to comment.')
      return
    }
    const updatedAnswers = pollAnswers.map(option => {
      if (option.option === answer) {
        return { ...option, votes: option.votes + 1 }
      }
      return option
    })
    if (userVoted) {
      updatedAnswers.forEach(option => {
        if (option.option === userVoted) {
          option.votes -= 1
        }
      })
    }
    comment.id = `${uid}-${Date.now()}`
    comment.sectionIndex = sectionIndex
    comment.pulseIndex = pulseIndex
    comment.option = answer
    const d = new Date()
    comment.time = d
    comment.dateString = d.toDateString()
    setUserVoted(answer)
    setPollAnswers(updatedAnswers)
    setComments([...comments, comment], comment)
    setCurrentComment({
      message: '',
      messageType: 'Comment',
      uid,
      displayName
    })
  }

  useEffect(() => {
    setIsAdmin(
      document &&
        (document.uid === uid ||
          (document.admins && document.admins.includes(uid)))
    )
  }, [document, uid])

  useEffect(() => {
    let commentsArray = comments.filter(
      comment =>
        comment.sectionIndex === sectionIndex &&
        comment.pulseIndex === pulseIndex
    )
    setPollAnswers(
      pulseValues.map(value => {
        return {
          option: value,
          votes: commentsArray.reduce((acc, curr) => {
            return curr.option === value ? acc + 1 : acc
          }, 0)
        }
      })
    )
    setUserVoted(
      commentsArray.find(comment => comment.uid === uid)?.option || null
    )
  }, [comments])

  const handleVote = answer => {
    if (!isAllowedToComment) {
      return
    }
    if (userVoted) {
      setAlert(
        'You have already responded, please remove your response to respond differently.'
      )
      return
    }
    setAlert(null)
    addComment(currentComment, answer)
  }

  const saveVote = valueTemp => {
    let valueToSave = typeof valueTemp === 'string' ? valueTemp : value
    setValue(null)
    if (valueToSave) {
      setTimeout(() => {
        handleVote(valueToSave)
        setSortType('All Comments')
        setClicked(false)
      }, 250)
    }
  }

  return (
    <Card
      raised
      sx={{ width: '100%', marginBottom: 2, padding: '0 !important' }}
    >
      <CardHeader
        title={
          <Typography
            type='span'
            sx={{
              fontSize: '1rem',
              fontWeight: 'bold',
              overflowWrap: 'anywhere'
            }}
            id={`pulse-prompt-` + sectionIndex + '-' + pulseIndex}
          >
            {pulsePrompt}
          </Typography>
        }
        action={
          !userVoted &&
          pulseIndex === 0 && (
            <>
              <Tooltip title='Pulse Section Type'>
                <Chip
                  color='secondary'
                  label='Slider'
                  onClick={() => setShowInfo(true)}
                />
              </Tooltip>
              <Tooltip title='Pulse Usage Directions'>
                <IconButton
                  sx={{ placeSelf: 'end' }}
                  color='secondary'
                  onClick={() => setShowInfo(true)}
                >
                  <ContactSupport />
                </IconButton>
              </Tooltip>
              <InfoDialog
                showInfo={showInfo}
                setShowInfo={setShowInfo}
                showSpecificSection='Slider'
              />
            </>
          )
        }
      ></CardHeader>
      <CardContent
        sx={{ padding: 0, paddingTop: 1, paddingBottom: '0 !Important' }}
      >
        {alert && (
          <PulseAlertMessage
            setOpen={setOpen}
            setAlert={setAlert}
            alert={alert}
            setSortType={setSortType}
          />
        )}
        {!userVoted && (
          <>
            <Box
              sx={{
                width: '80%',
                margin: '16px auto 8px auto',
                display: { md: 'block', xs: 'none' },
                opacity:
                  (isAdmin && isPreview && !isAllowedToComment) ||
                  !isAllowedToComment
                    ? 0.6
                    : 1,
                pointerEvents:
                  ((isAdmin && isPreview && !isAllowedToComment) ||
                    !isAllowedToComment) &&
                  'none'
              }}
              onClick={
                (isAdmin && isPreview && !isAllowedToComment) ||
                !isAllowedToComment
                  ? e => {
                      e.stopPropagation()
                    }
                  : null
              }
            >
              <Slider
                onChangeCommitted={(e, value) => {
                  if (
                    e.type === 'mouseup' ||
                    (e.type === 'keydown' && e.key === 'Enter')
                  ) {
                    setClicked(true)
                    setValue(pulseValues[value])
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    setClicked(true)
                    saveVote(pulseValues[e.target.value])
                  }
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    if (e.key === 'ArrowLeft' && e.target.value > 0) {
                      setValue(pulseValues[parseInt(e.target.value) - 1])
                    } else if (
                      e.key === 'ArrowRight' &&
                      e.target.value < pulseValues.length - 1
                    ) {
                      setValue(pulseValues[parseInt(e.target.value) + 1])
                    }
                    setClicked(true)
                  }
                }}
                onBlur={saveVote}
                className={clicked ? 'pulse-slider-clicked' : ''}
                sx={{
                  mb: 4,
                  '& .MuiSlider-mark': {
                    background: theme => theme.palette.primary.main
                  },
                  '& .MuiSlider-markLabel': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    padding: '2px 8px',
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    marginTop: 1,
                    lineHeight: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: `calc((100vw / ${pulseValues.length}) - 65px)`,
                    '&:hover': {
                      opacity: '0.9'
                    }
                  }
                }}
                defaultValue={-1}
                valueLabelDisplay='off'
                marks={pulseValues.map((value, index) => ({
                  value: index,
                  label: value
                }))}
                getAriaValueText={index => pulseValues[index]}
                step={null}
                min={0}
                max={pulseValues.length - 1}
              />
            </Box>
            <Box
              sx={{
                margin: '16px auto 8px auto',
                display: { md: 'none', xs: 'block' }
              }}
            >
              <Slider
                onChangeCommitted={(e, value) => {
                  if (
                    e.type === 'mouseup' ||
                    (e.type === 'keydown' && e.key === 'Enter')
                  ) {
                    setClicked(true)
                    setValue(pulseValues[value])
                  }
                }}
                orientation='vertical'
                className={clicked ? 'pulse-slider-clicked' : ''}
                sx={{
                  height: pulseValues.length * 56,
                  mb: 4,
                  '& .MuiSlider-mark': {
                    background: theme => theme.palette.primary.main
                  },
                  '& .MuiSlider-markLabel': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    padding: '2px 8px',
                    borderRadius: '16px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    marginTop: 1,
                    lineHeight: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 'calc(100vw - 150px)',
                    '&:hover': {
                      opacity: '0.9'
                    }
                  }
                }}
                defaultValue={-1}
                valueLabelDisplay='off'
                marks={pulseValues.map((value, index) => ({
                  value: index,
                  label: value
                }))}
                getAriaValueText={index => pulseValues[index]}
                step={null}
                min={0}
                max={pulseValues.length - 1}
              />
            </Box>
            {isAdmin && !isPreview && (
              <Button
                variant='contained'
                disabled={!value || !isAllowedToComment}
                onClick={saveVote}
                sx={{ m: 2, mt: 0, float: 'right' }}
              >
                Save
              </Button>
            )}
          </>
        )}
        <Stack
          direction='column'
          spacing={2}
          sx={{
            width: 'min(100%, 1000px)',
            justifyContent: { xs: 'center' },
            flexWrap: { md: 'nowrap', xs: 'wrap' },
            p: 2,
            margin: 'auto !important',
            clear: 'both'
          }}
        >
          {pollAnswers.map((answer, index) => (
            <React.Fragment
              key={index + '_answer' + sectionIndex + '_section' + answer}
            >
              {(userVoted || (isAdmin && !isPreview)) && (
                <Stack
                  tabIndex={0}
                  onClick={() => handleVote(answer.option)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleVote(answer.option)
                    }
                  }}
                  aria-labelledby={
                    `pulse-answer-` +
                    sectionIndex +
                    '-' +
                    pulseIndex +
                    '-' +
                    index
                  }
                  style={{
                    width: '100%'
                  }}
                >
                  <Stack
                    direction={'row'}
                    justifyContent='space-between'
                    alignItems='baseline'
                    sx={{ flexWrap: 'wrap' }}
                    columnGap={2}
                    id={
                      `pulse-answer-` +
                      sectionIndex +
                      '-' +
                      pulseIndex +
                      '-' +
                      index
                    }
                  >
                    {answer.option}
                    {(userVoted || isAdmin) && (
                      <Typography variant='span' sx={{ ml: 'auto' }}>
                        {userVoted === answer.option && (
                          <Check sx={{ top: '6px', position: 'relative' }} />
                        )}
                        {answer.votes +
                          ' response' +
                          (answer.votes === 1 ? '' : 's') +
                          (pollAnswers.reduce(
                            (acc, curr) => acc + curr.votes,
                            0
                          ) > 0
                            ? ' (' +
                              Math.floor(
                                (answer.votes /
                                  pollAnswers.reduce(
                                    (acc, curr) => acc + curr.votes,
                                    0
                                  )) *
                                  100
                              ) +
                              '%)'
                            : '')}
                      </Typography>
                    )}
                  </Stack>
                  <LinearProgress
                    variant='determinate'
                    sx={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '4px',
                      [`&.${linearProgressClasses.colorPrimary}`]: {
                        backgroundColor: '#d3d3d387'
                      },
                      [`& .${linearProgressClasses.bar}`]: {
                        backgroundColor:
                          userVoted === answer.option
                            ? undefined
                            : 'lightsteelblue'
                      }
                    }}
                    value={
                      userVoted || isAdmin
                        ? (answer.votes /
                            pollAnswers.reduce(
                              (acc, curr) => acc + curr.votes,
                              0
                            )) *
                            100 || -1
                        : -1
                    }
                  />
                </Stack>
              )}
            </React.Fragment>
          ))}
        </Stack>
        <CardActions
          sx={{ justifyContent: 'flex-end', paddingRight: 2, paddingBottom: 2 }}
        >
          {(!isAdmin || isPreview) && !userVoted && (
            <Button
              variant='contained'
              disabled={!value || !isAllowedToComment}
              onClick={saveVote}
            >
              Save
            </Button>
          )}
          {(userVoted || (isAdmin && !isPreview)) && (
            <>
              <Tooltip title='View All Responses'>
                <Button
                  onClick={() => {
                    setSortType('All Comments')
                    setShowEditModal(false)
                    setOpen(true)
                  }}
                >
                  <Forum />
                </Button>
              </Tooltip>
              {userVoted && (
                <Tooltip title='Add or Edit Your Response'>
                  <Button
                    onClick={() => {
                      setSortType('Yours')
                      setOpen(true)
                      setShowEditModal(true)
                    }}
                    variant='contained'
                  >
                    <AddComment />
                  </Button>
                </Tooltip>
              )}
              {pulseTotalPrompts - 1 === pulseIndex && hasNextSection(section) && (
                <Tooltip title='Next Section'>
                  <Button onClick={() => goToNextSection(section)}>
                    <KeyboardDoubleArrowDown />
                  </Button>
                </Tooltip>
              )}
              <Drawer
                open={open}
                onClose={toggleDrawer(false)}
                anchor='right'
                sx={{
                  overflow: 'visible',
                  '& .MuiDrawer-paper': {
                    maxWidth: { xs: '100%', md: '50%' },
                    minWidth: { xs: '100%', md: '50%' }
                  }
                }}
              >
                <PulseComments
                  comments={comments}
                  setComments={setComments}
                  pulseValues={pulseValues}
                  isAllowedToComment={isAllowedToComment}
                  uid={uid}
                  isAdmin={isAdmin}
                  document={document}
                  userVoted={userVoted}
                  sectionIndex={sectionIndex}
                  pulseIndex={pulseIndex}
                  sortType={sortType}
                  setSortType={setSortType}
                  prompt={pulsePrompt}
                  setAlert={setAlert}
                  toggleDrawer={setOpen}
                  showEditModal={showEditModal}
                />
              </Drawer>
            </>
          )}
        </CardActions>
      </CardContent>
    </Card>
  )
}

export default PulseComponent
